import { useCallback, useState } from 'react';

import { FilterOption } from '@/components/ui/FilterSelect/FilterSelect';

type FilterState = {
  [key: string]: FilterOption[];
};

type UseFilterTagsParams = {
  filterNames: string[];
};

type UseFilterTagsReturn = {
  filters: FilterState;
  updateFilter: (filterName: string, items: FilterOption[]) => void;
  removeFilter: (filterId: string) => void;
  clearAllFilters: () => void;
  createFilterTags: (items: FilterOption[], type: string) => Array<{
    id: string;
    label: string;
    value: string;
    type: string;
  }>;
};

export const useFilterTags = ({ filterNames }: UseFilterTagsParams): UseFilterTagsReturn => {
  const [filters, setFilters] = useState<FilterState>(() => {
    const initialState: FilterState = {};
    filterNames.forEach(name => {
      initialState[name] = [];
    });

    return initialState;
  });

  const updateFilter = useCallback((filterName: string, items: FilterOption[]) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: items
    }));
  }, []);

  const removeFilter = useCallback((filterId: string) => {
    setFilters(prev => {
      const newFilters = { ...prev };

      for (const filterName of filterNames) {
        if (filterId.startsWith(`${filterName}-`)) {
          const value = filterId.replace(`${filterName}-`, '');
          newFilters[filterName] = prev[filterName].filter(item => item.value !== value);
          break;
        }
      }

      return newFilters;
    });
  }, [filterNames]);

  const clearAllFilters = useCallback(() => {
    setFilters(prev => {
      const newFilters = { ...prev };
      filterNames.forEach(name => {
        newFilters[name] = [];
      });

      return newFilters;
    });
  }, [filterNames]);

  const createFilterTags = useCallback((items: FilterOption[], type: string) => {
    return items.map(item => ({
      id: `${type.toLowerCase()}-${item.value}`,
      label: item.label,
      value: item.value,
      type: type.charAt(0).toUpperCase() + type.slice(1)
    }));
  }, []);

  return {
    filters,
    updateFilter,
    removeFilter,
    clearAllFilters,
    createFilterTags
  };
};
